/** Persists admin shell across browser restarts (with Supabase Auth session). */

import type { AppUserRole } from '../types/roles';

export const ADMIN_AUTH_KEY = 'hyperpromax_admin_authenticated';
/** Current JSON shape: {@link AdminStaffSession} */
export const ADMIN_SESSION_KEY = 'hyperpromax_admin_staff_session';
/** Legacy key from access-table era; read once to migrate. */
const LEGACY_ADMIN_ACCESS_KEY = 'hyperpromax_admin_access_record';

export interface AdminStaffSession {
  hy_user_id: string;
  name: string;
  role: 'admin' | 'superadmin';
}

function migrateLegacyJson(raw: string): AdminStaffSession | null {
  try {
    const r = JSON.parse(raw) as Record<string, unknown>;
    const hy = typeof r.hy_user_id === 'string' ? r.hy_user_id.trim() : '';
    if (!hy) return null;
    const name = typeof r.name === 'string' ? r.name.trim() : 'Admin';
    const issuperadmin = r.issuperadmin === true;
    const isadmin = r.isadmin === true;
    const role: 'admin' | 'superadmin' = issuperadmin ? 'superadmin' : isadmin ? 'admin' : 'admin';
    return { hy_user_id: hy, name: name || 'Admin', role };
  } catch {
    return null;
  }
}

export function getAdminSessionJson(): string | null {
  return (
    localStorage.getItem(ADMIN_SESSION_KEY) ??
    localStorage.getItem(LEGACY_ADMIN_ACCESS_KEY) ??
    sessionStorage.getItem('admin_access_record')
  );
}

export function parseAdminStaffSession(): AdminStaffSession | null {
  const raw = getAdminSessionJson();
  if (!raw) return null;
  try {
    const r = JSON.parse(raw) as Record<string, unknown>;
    if (typeof r.hy_user_id === 'string' && (r.role === 'admin' || r.role === 'superadmin')) {
      return {
        hy_user_id: r.hy_user_id.trim(),
        name: typeof r.name === 'string' ? r.name.trim() : 'Admin',
        role: r.role,
      };
    }
  } catch {
    return null;
  }
  const migrated = migrateLegacyJson(raw);
  if (migrated) {
    setAdminSessionPersisted(migrated);
    localStorage.removeItem(LEGACY_ADMIN_ACCESS_KEY);
  }
  return migrated;
}

/** @deprecated Use {@link parseAdminStaffSession} */
export function parseAdminAccessRecord(): AdminStaffSession | null {
  return parseAdminStaffSession();
}

export function clearAdminSessionStorage(): void {
  localStorage.removeItem(ADMIN_AUTH_KEY);
  localStorage.removeItem(ADMIN_SESSION_KEY);
  localStorage.removeItem(LEGACY_ADMIN_ACCESS_KEY);
  sessionStorage.removeItem('admin_authenticated');
  sessionStorage.removeItem('admin_access_record');
}

export function setAdminSessionPersisted(record: AdminStaffSession): void {
  localStorage.setItem(ADMIN_AUTH_KEY, 'true');
  localStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify(record));
  localStorage.removeItem(LEGACY_ADMIN_ACCESS_KEY);
  sessionStorage.removeItem('admin_authenticated');
  sessionStorage.removeItem('admin_access_record');
}

export function appRoleFromStaffSession(s: AdminStaffSession | null): AppUserRole | null {
  if (!s) return null;
  return s.role;
}
